import { useState, useCallback, useEffect, useMemo } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  ChevronRight,
  OpenInNew,
  Description,
  ContentCopy,
  Save,
  Add,
  Delete,
  DragIndicator,
  ExpandMore,
  ExpandLess,
  Edit,
  AutoAwesome,
  History,
  Science,
} from '@mui/icons-material';
import {
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  Tooltip,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  Alert,
  AlertTitle,
  Collapse,
  Box,
  Typography,
  Divider,
  CircularProgress,
  OutlinedInput,
  Checkbox,
  ListItemText,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { AppHeader } from '../components/app-header';
import { BackButton } from '../components/back-button';
import { LoadingState } from '../components/loading-state';
import { ShadowNoteTestDialog } from '../components/shadow-note-test-dialog';
import { VersionHistoryDrawer } from '../components/version-history-drawer';
import { useGetProviderInfoQuery } from '../store/api';
import {
  useGetAdminNoteTemplateQuery,
  useUpdateAdminNoteTemplateMutation,
  useAdminBuildFromTemplateMutation,
  type AdminUpdateNoteTemplateRequest,
  type AdminNoteTemplateSectionRequest,
  type NoteTemplateSection2,
} from '../store/hooks';

// Model names mapping
const MODEL_NAMES: Record<string, string> = {
  'gpt-5-mini': 'GPT-5 Mini',
  'gpt-5-nano': 'GPT-5 Nano',
  'gpt-5': 'GPT-5',
  'gpt-4o': 'GPT-4o',
  'claude-4-5-sonnet': 'Claude 4.5 Sonnet',
  'claude-3-5-sonnet': 'Claude 3.5 Sonnet',
  'gpt-4': 'GPT-4',
  'gpt-3.5-turbo': 'GPT-3.5-Turbo',
  'gpt-4-turbo': 'GPT-4-Turbo (OpenAI)',
  'azure-gpt-4-turbo': 'GPT-4-Turbo (Azure)',
  'claude-3-opus': 'Claude 3 Opus',
  'claude-3-sonnet': 'Claude 3 Sonnet',
};

// Section item component with drag-and-drop
interface SortableSectionItemProps {
  section: EditableSection;
  index: number;
  onUpdate: (id: string, updates: Partial<EditableSection>) => void;
  onRemove: (id: string) => void;
}

function SortableSectionItem({ section, index, onUpdate, onRemove }: SortableSectionItemProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: section.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Card
      ref={setNodeRef}
      style={style}
      sx={{ mb: 2, border: isDragging ? '2px dashed' : '1px solid', borderColor: isDragging ? 'primary.main' : 'divider' }}
    >
      <CardContent sx={{ py: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: isExpanded ? 2 : 0 }}>
          {/* Drag handle */}
          <IconButton
            {...attributes}
            {...listeners}
            size="small"
            sx={{ cursor: 'grab', '&:active': { cursor: 'grabbing' } }}
          >
            <DragIndicator />
          </IconButton>

          {/* Section title */}
          <TextField
            size="small"
            label="Section Title"
            value={section.title}
            onChange={e => onUpdate(section.id, { title: e.target.value })}
            sx={{ flex: 1, maxWidth: 300 }}
          />

          {/* Order indicator */}
          <Typography variant="body2" color="text.secondary" sx={{ minWidth: 60 }}>
            Order: {index}
          </Typography>

          {/* Expand/collapse */}
          <IconButton onClick={() => setIsExpanded(!isExpanded)} size="small">
            {isExpanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>

          {/* Delete */}
          <Tooltip title="Remove Section">
            <IconButton onClick={() => onRemove(section.id)} size="small" color="error">
              <Delete />
            </IconButton>
          </Tooltip>
        </Box>

        <Collapse in={isExpanded}>
          <Divider sx={{ my: 2 }} />

          {/* Section ID */}
          <TextField
            fullWidth
            size="small"
            label="Section ID"
            value={section.id}
            disabled
            sx={{ mb: 2 }}
            helperText="Section ID cannot be changed"
          />

          {/* Content (generation prompt) */}
          <TextField
            fullWidth
            multiline
            minRows={4}
            maxRows={12}
            label="Content (Generation Prompt)"
            value={section.content}
            onChange={e => onUpdate(section.id, { content: e.target.value })}
            sx={{ mb: 2 }}
            helperText="The prompt used to generate this section"
          />

          {/* Example output */}
          <TextField
            fullWidth
            multiline
            minRows={3}
            maxRows={8}
            label="Example Output"
            value={section.example_output || ''}
            onChange={e => onUpdate(section.id, { example_output: e.target.value || null })}
            sx={{ mb: 2 }}
            helperText="Optional example of what this section generates"
          />

          {/* Legacy module ID (read-only) */}
          {section.legacy_module_id && (
            <TextField
              fullWidth
              size="small"
              label="Legacy Module ID"
              value={section.legacy_module_id}
              disabled
              sx={{ mb: 2 }}
             slotProps={{ input: {
                endAdornment: (
                  <Tooltip title="Copy">
                    <IconButton
                      size="small"
                      onClick={() => {
                        void navigator.clipboard.writeText(section.legacy_module_id!);
                      }}
                    >
                      <ContentCopy fontSize="small" />
                    </IconButton>
                  </Tooltip>
                ),
              } }}/>
          )}
        </Collapse>
      </CardContent>
    </Card>
  );
}

// Editable section type (local state)
interface EditableSection {
  id: string;
  title: string;
  content: string;
  order: number;
  example_output: string | null;
  legacy_module_id: string | null;
}

// Transform API section to editable section
function toEditableSection(section: NoteTemplateSection2, index: number): EditableSection {
  return {
    id: section.id,
    title: section.title,
    content: section.content,
    order: section.order ?? index,
    example_output: section.example_output ?? null,
    legacy_module_id: section.legacy_module_id ?? null,
  };
}

// Transform editable section to API request
function toSectionRequest(section: EditableSection, index: number): AdminNoteTemplateSectionRequest {
  return {
    id: section.id,
    title: section.title,
    content: section.content,
    order: index,
    example_output: section.example_output,
    legacy_module_id: section.legacy_module_id,
  };
}

export default function TemplateDetailPage() {
  const { providerId: urlProviderId, templateId } = useParams<{
    providerId?: string;
    templateId: string;
  }>();
  const [searchParams] = useSearchParams();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  // Decode URL parameters
  const providerId = urlProviderId ? decodeURIComponent(urlProviderId) : null;

  // Local state for editing
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [prompt, setPrompt] = useState('');
  const [ownerScope, setOwnerScope] = useState<'public' | 'org' | 'user'>('user');
  const [organizationId, setOrganizationId] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [allowUserForks, setAllowUserForks] = useState(false);
  const [sharable, setSharable] = useState(false);
  const [sections, setSections] = useState<EditableSection[]>([]);
  const [models, setModels] = useState<string[]>([]);
  const [noteProcessingVersion, setNoteProcessingVersion] = useState<number>(2);
  const [hasChanges, setHasChanges] = useState(false);
  const [metadataExpanded, setMetadataExpanded] = useState(false);
  const [historyDrawerOpen, setHistoryDrawerOpen] = useState(false);
  const [shadowNoteDialogOpen, setShadowNoteDialogOpen] = useState(false);

  // Public template confirmation checkboxes (the "are you REALLY sure?" gauntlet)
  const [confirmUnderstand, setConfirmUnderstand] = useState(false);
  const [confirmThoughtAboutIt, setConfirmThoughtAboutIt] = useState(false);
  const [confirmAbsolutelySure, setConfirmAbsolutelySure] = useState(false);

  // Fetch provider info (if provider context)
  const { data: provider } = useGetProviderInfoQuery(
    { providerId: providerId || '' },
    { skip: !providerId },
  );

  // Fetch template details using admin API
  const {
    data: templateData,
    isLoading,
    error: loadError,
    refetch,
  } = useGetAdminNoteTemplateQuery(
    { templateId: templateId || '' },
    { skip: !templateId },
  );

  // Update mutation
  const [updateTemplate, { isLoading: isUpdating }] = useUpdateAdminNoteTemplateMutation();

  // Admin build mutation (for Edit with AI)
  const [adminBuildFromTemplate, { isLoading: isBuilding }] = useAdminBuildFromTemplateMutation();

  // Reset confirmations when template data changes
  useEffect(() => {
    setConfirmUnderstand(false);
    setConfirmThoughtAboutIt(false);
    setConfirmAbsolutelySure(false);
  }, [templateData?.template?.id]);

  // For public templates, all three checkboxes must be checked to enable save
  const canSavePublicTemplate = ownerScope !== 'public' || 
    (confirmUnderstand && confirmThoughtAboutIt && confirmAbsolutelySure);

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // Initialize form state from API response
  useEffect(() => {
    if (templateData) {
      setTitle(templateData.revision.title || '');
      setDescription(templateData.revision.description || '');
      setPrompt(templateData.revision.prompt || '');
      setOwnerScope(templateData.template.owner_scope as 'public' | 'org' | 'user');
      setOrganizationId(templateData.template.organization_id || null);
      setUserId(templateData.template.user_id || null);
      setAllowUserForks(templateData.template.allow_user_forks ?? false);
      setSharable(templateData.template.sharable ?? false);
      setSections(
        (templateData.revision.sections || []).map((s, i) => toEditableSection(s, i)),
      );
      // Initialize models from revision (may not be in type yet, but will be after codegen)
      setModels(
        (templateData.revision as any).models || ['gpt-5-mini', 'claude-4-5-sonnet'],
      );
      // Initialize processing version (defaults to 2 if not set)
      setNoteProcessingVersion(templateData.template.note_processing_version ?? 2);
      setHasChanges(false);
    }
  }, [templateData]);

  // Track changes
  const markChanged = useCallback(() => {
    setHasChanges(true);
  }, []);

  // Section handlers
  const handleUpdateSection = useCallback((id: string, updates: Partial<EditableSection>) => {
    setSections(prev =>
      prev.map(s => (s.id === id ? { ...s, ...updates } : s)),
    );
    markChanged();
  }, [markChanged]);

  const handleRemoveSection = useCallback((id: string) => {
    setSections(prev => prev.filter(s => s.id !== id));
    markChanged();
  }, [markChanged]);

  const handleAddSection = useCallback(() => {
    const newId = `section-${Date.now()}`;
    setSections(prev => [
      ...prev,
      {
        id: newId,
        title: 'New Section',
        content: '',
        order: prev.length,
        example_output: null,
        legacy_module_id: null,
      },
    ]);
    markChanged();
  }, [markChanged]);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setSections(prev => {
      const oldIndex = prev.findIndex(s => s.id === active.id);
      const newIndex = prev.findIndex(s => s.id === over.id);
      return arrayMove(prev, oldIndex, newIndex);
    });
    markChanged();
  }, [markChanged]);

  // Handle Edit with AI - launch template builder on behalf of user
  const handleEditWithAI = useCallback(async () => {
    if (!templateId || !providerId) {
      enqueueSnackbar('Provider ID is required to edit template with AI', { variant: 'error' });
      return;
    }

    // Get target organization ID from provider or template data
    const targetOrgId = provider?.organization_id || templateData?.template.organization_id;
    if (!targetOrgId) {
      enqueueSnackbar('Organization ID is required to edit template with AI', { variant: 'error' });
      return;
    }

    try {
      const result = await adminBuildFromTemplate({
        adminTemplateBuildFromTemplateRequest: {
          template_id: templateId,
          target_user_id: providerId,
          target_organization_id: targetOrgId,
        },
      }).unwrap();

      // Navigate to template builder with admin mode
      navigate(`/templates/build/${result.task_id}?admin=true&targetUserId=${providerId}`);
    } catch (error: any) {
      console.error('Failed to start AI edit:', error);
      enqueueSnackbar(
        error?.data?.message || 'Failed to start AI edit. Please try again.',
        { variant: 'error' },
      );
    }
  }, [
    templateId,
    providerId,
    provider?.organization_id,
    templateData?.template.organization_id,
    adminBuildFromTemplate,
    navigate,
    enqueueSnackbar,
  ]);

  // Save handler
  const handleSave = useCallback(async () => {
    if (!templateId) return;

    try {
      // Build partial update request
      const request: AdminUpdateNoteTemplateRequest = {};

      // Only include fields that have values
      if (title.trim()) request.title = title.trim();
      if (description.trim()) request.description = description.trim();
      if (prompt.trim()) request.prompt = prompt.trim();
      request.owner_scope = ownerScope;
      if (ownerScope !== 'public' && organizationId) {
        request.organization_id = organizationId;
      }
      if (ownerScope === 'user' && userId) {
        request.user_id = userId;
      }
      request.allow_user_forks = allowUserForks;
      request.sharable = sharable;

      // Include models
      request.models = models;

      // Include processing version
      request.note_processing_version = noteProcessingVersion;

      // Include sections
      request.sections = sections.map((s, i) => toSectionRequest(s, i));

      const result = await updateTemplate({
        templateId,
        adminUpdateNoteTemplateRequest: request,
      }).unwrap();

      enqueueSnackbar(result.message || 'Template updated successfully', { variant: 'success' });
      setHasChanges(false);
      refetch();
    } catch (error: any) {
      enqueueSnackbar(error?.data?.message || 'Failed to update template', { variant: 'error' });
    }
  }, [
    templateId,
    title,
    description,
    prompt,
    ownerScope,
    organizationId,
    userId,
    allowUserForks,
    sharable,
    models,
    noteProcessingVersion,
    sections,
    updateTemplate,
    enqueueSnackbar,
    refetch,
  ]);

  // Section IDs for DnD
  const sectionIds = useMemo(() => sections.map(s => s.id), [sections]);

  // Navigate to comparison page
  const handleCompareVersions = useCallback((versions: number[]) => {
    const basePath = providerId
      ? `/admin-v2/provider/${encodeURIComponent(providerId)}/templates/${templateId}/compare`
      : `/admin-v2/templates/${templateId}/compare`;
    navigate(`${basePath}?versions=${versions.join(',')}`);
  }, [providerId, templateId, navigate]);

  // Error state
  if (!templateId) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AppHeader />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900">Invalid URL parameters</h2>
            <p className="text-gray-600 mt-2">Missing template ID.</p>
            <Link to="/admin-v2">
              <Button className="mt-4">Back to Search</Button>
            </Link>
          </div>
        </main>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AppHeader />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <LoadingState message="Loading template details..." />
        </main>
      </div>
    );
  }

  if (loadError || !templateData) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AppHeader />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900">Template not found</h2>
            <p className="text-gray-600 mt-2">Unable to load template details.</p>
            <Link to="/admin-v2">
              <Button className="mt-4">Back to Search</Button>
            </Link>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <AppHeader />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-4">
          <BackButton />
        </div>

        {/* Breadcrumb */}
        <nav className="flex mb-6" aria-label="Breadcrumb">
          <ol className="inline-flex items-center space-x-1 md:space-x-3">
            <li className="inline-flex items-center">
              <Link to="/admin-v2">
                <Button
                  variant="text"
                  className="text-sm font-medium text-gray-700 hover:text-blue-600"
                >
                  <OpenInNew className="mr-2 h-4 w-4" />
                  Search
                </Button>
              </Link>
            </li>
            {providerId && (
              <li>
                <div className="flex items-center">
                  <ChevronRight className="h-4 w-4 text-gray-400 mx-2" />
                  <Link
                    to={`/admin-v2/provider/${encodeURIComponent(providerId)}`}
                    className="ml-1 text-sm font-medium text-gray-700 hover:text-blue-600 md:ml-2"
                  >
                    {provider?.name || 'Provider'}
                  </Link>
                </div>
              </li>
            )}
            <li>
              <div className="flex items-center">
                <ChevronRight className="h-4 w-4 text-gray-400 mx-2" />
                <span className="ml-1 text-sm font-medium text-gray-500 md:ml-2">
                  {title || 'Template Editor'}
                </span>
              </div>
            </li>
          </ol>
        </nav>

        {/* Scope warning banner for public/org templates */}
        {ownerScope !== 'user' && (
          <Alert 
            severity={ownerScope === 'public' ? 'error' : 'warning'} 
            sx={{ 
              mb: 3,
              ...(ownerScope === 'public' && {
                border: '2px solid',
                borderColor: 'error.main',
                '& .MuiAlert-icon': { fontSize: 28 },
              }),
            }}
          >
            <AlertTitle sx={{ fontWeight: 'bold', fontSize: ownerScope === 'public' ? '1.1rem' : undefined }}>
              {ownerScope === 'public' ? '🚨 DANGER ZONE: Editing PUBLIC Template 🚨' : 'Editing Shared Template'}
            </AlertTitle>
            {ownerScope === 'public' ? (
              <>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>⚠️ HOLD UP.</strong> This template is visible to <strong>LITERALLY EVERYONE</strong>.
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  Any changes you make will immediately affect <strong>ALL USERS</strong> across the <strong>ENTIRE PLATFORM</strong>.
                  This is not a drill. This is not a test. There is no undo button. Please confirm you have fully considered the gravity of this situation.
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body2" sx={{ mb: 2, fontWeight: 'bold' }}>
                  Complete the confirmation gauntlet to unlock the Save button:
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={confirmUnderstand}
                        onChange={e => setConfirmUnderstand(e.target.checked)}
                        color="error"
                      />
                    }
                    label={
                      <Typography variant="body2">
                        ☝️ I understand this affects <strong>ALL USERS</strong> on the platform
                      </Typography>
                    }
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={confirmThoughtAboutIt}
                        onChange={e => setConfirmThoughtAboutIt(e.target.checked)}
                        color="error"
                        disabled={!confirmUnderstand}
                      />
                    }
                    label={
                      <Typography variant="body2" color={!confirmUnderstand ? 'text.disabled' : 'inherit'}>
                        ✌️ I have really, <em>really</em>, <strong>REALLY</strong> thought about this
                      </Typography>
                    }
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={confirmAbsolutelySure}
                        onChange={e => setConfirmAbsolutelySure(e.target.checked)}
                        color="error"
                        disabled={!confirmThoughtAboutIt}
                      />
                    }
                    label={
                      <Typography variant="body2" color={!confirmThoughtAboutIt ? 'text.disabled' : 'inherit'}>
                        🤟 I am <strong>ABSOLUTELY, POSITIVELY, 100% CERTAIN</strong> and accept full responsibility 🫡
                      </Typography>
                    }
                  />
                </Box>
                {confirmAbsolutelySure && (
                  <Box sx={{ mt: 2, p: 1.5, bgcolor: 'success.light', borderRadius: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'success.dark' }}>
                      ✅ Alright brave soul, you've passed the gauntlet. The Save button is now enabled. May your changes be bug-free and your users forgiving. Godspeed. 🚀
                    </Typography>
                  </Box>
                )}
              </>
            ) : (
              <Typography variant="body2">
                This is an organization template. Changes will affect all users in the organization.
              </Typography>
            )}
          </Alert>
        )}

        {/* Unsaved changes alert */}
        {hasChanges && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            You have unsaved changes. Click "Save Changes" to persist your edits.
          </Alert>
        )}

        {/* Template Header with Save Button */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="h-16 w-16 rounded-full bg-blue-100 flex items-center justify-center">
                  <Description className="h-8 w-8 text-blue-600" />
                </div>
                <div className="ml-6">
                  <TextField
                    value={title}
                    onChange={e => {
                      setTitle(e.target.value);
                      markChanged();
                    }}
                    variant="standard"
                    placeholder="Template Name"
                    sx={{ minWidth: 300 }}
                   slotProps={{ input: {
                      style: { fontSize: '1.5rem', fontWeight: 'bold' },
                    } }}/>
                  <div className="flex items-center gap-4 mt-2">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        ID:
                      </Typography>
                      <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                        {templateId}
                      </Typography>
                      <Tooltip title="Copy Template ID">
                        <IconButton
                          size="small"
                          onClick={() => {
                            void navigator.clipboard.writeText(templateId);
                            enqueueSnackbar('Template ID copied', { variant: 'info' });
                          }}
                        >
                          <ContentCopy fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      Version: {templateData.revision.version}
                    </Typography>
                    <Chip
                      label={ownerScope}
                      size="small"
                      color={
                        ownerScope === 'public'
                          ? 'primary'
                          : ownerScope === 'org'
                            ? 'success'
                            : 'default'
                      }
                    />
                    <Chip
                      label={`Processing V${noteProcessingVersion}`}
                      size="small"
                      color={noteProcessingVersion === 2 ? 'info' : 'default'}
                      variant={noteProcessingVersion === 2 ? 'filled' : 'outlined'}
                    />
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Typography variant="body2" color="text.secondary">
                  {sections.length} section{sections.length !== 1 ? 's' : ''}
                </Typography>
                <Tooltip title="Version history & comparison">
                  <IconButton
                    onClick={() => setHistoryDrawerOpen(true)}
                    size="small"
                    sx={{ border: 1, borderColor: 'divider' }}
                  >
                    <History fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Test this template against a real encounter transcript">
                  <Button
                    variant="outlined"
                    color="secondary"
                    startIcon={<Science />}
                    onClick={() => setShadowNoteDialogOpen(true)}
                  >
                    Test Template
                  </Button>
                </Tooltip>
                {providerId && (
                  <Tooltip title="Edit with AI (on behalf of user)">
                    <Button
                      variant="outlined"
                      color="primary"
                      startIcon={isBuilding ? <CircularProgress size={16} color="inherit" /> : <AutoAwesome />}
                      onClick={handleEditWithAI}
                      disabled={isBuilding || isUpdating}
                    >
                      {isBuilding ? 'Loading...' : 'Edit with AI'}
                    </Button>
                  </Tooltip>
                )}
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={isUpdating ? <CircularProgress size={16} color="inherit" /> : <Save />}
                  onClick={handleSave}
                  disabled={isUpdating || !hasChanges || !canSavePublicTemplate}
                >
                  {isUpdating ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Template Details Card */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Template Details
            </Typography>

            {/* Description */}
            <TextField
              fullWidth
              label="Description"
              value={description}
              onChange={e => {
                setDescription(e.target.value);
                markChanged();
              }}
              multiline
              minRows={2}
              maxRows={4}
              sx={{ mb: 3 }}
              helperText="A brief description of what this template is for"
            />

            {/* Global Prompt */}
            <TextField
              fullWidth
              label="Global Prompt"
              value={prompt}
              onChange={e => {
                setPrompt(e.target.value);
                markChanged();
              }}
              multiline
              minRows={3}
              maxRows={8}
              sx={{ mb: 3 }}
              helperText="Global instructions that apply to all sections in this template"
            />
          </CardContent>
        </Card>

        {/* Admin Metadata Panel (Collapsible) */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box
              onClick={() => setMetadataExpanded(!metadataExpanded)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
              }}
            >
              <Typography variant="h6">
                Admin Settings
              </Typography>
              <IconButton size="small">
                {metadataExpanded ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            </Box>

            <Collapse in={metadataExpanded}>
              <Divider sx={{ my: 2 }} />

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
                {/* Owner Scope */}
                <FormControl fullWidth>
                  <InputLabel>Owner Scope</InputLabel>
                  <Select
                    value={ownerScope}
                    label="Owner Scope"
                    onChange={e => {
                      setOwnerScope(e.target.value as 'public' | 'org' | 'user');
                      markChanged();
                    }}
                  >
                    <MenuItem value="public">Public (visible to all)</MenuItem>
                    <MenuItem value="org">Organization</MenuItem>
                    <MenuItem value="user">User (private)</MenuItem>
                  </Select>
                </FormControl>

                {/* Processing Version */}
                <FormControl fullWidth>
                  <InputLabel>Processing Version</InputLabel>
                  <Select
                    value={noteProcessingVersion}
                    label="Processing Version"
                    onChange={e => {
                      setNoteProcessingVersion(e.target.value as number);
                      markChanged();
                    }}
                  >
                    <MenuItem value={1}>V1 Processing (Legacy)</MenuItem>
                    <MenuItem value={2}>V2 Processing (New with streaming)</MenuItem>
                  </Select>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.75 }}>
                    V1: Legacy processing flow | V2: New streaming-enabled processing
                  </Typography>
                </FormControl>

                {/* Organization ID (for org/user scope) */}
                {ownerScope !== 'public' && (
                  <TextField
                    fullWidth
                    label="Organization ID"
                    value={organizationId || ''}
                    onChange={e => {
                      setOrganizationId(e.target.value || null);
                      markChanged();
                    }}
                    helperText="Required for org and user scope"
                  />
                )}

                {/* User ID (for user scope) */}
                {ownerScope === 'user' && (
                  <TextField
                    fullWidth
                    label="User ID (Owner)"
                    value={userId || ''}
                    onChange={e => {
                      setUserId(e.target.value || null);
                      markChanged();
                    }}
                    helperText="The user who owns this template"
                  />
                )}

                {/* Toggles */}
                <Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={allowUserForks}
                        onChange={e => {
                          setAllowUserForks(e.target.checked);
                          markChanged();
                        }}
                      />
                    }
                    label="Allow User Forks"
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                    Whether users can fork/customize this template
                  </Typography>
                </Box>

                <Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={sharable}
                        onChange={e => {
                          setSharable(e.target.checked);
                          markChanged();
                        }}
                      />
                    }
                    label="Sharable"
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                    Whether this template can be shared via link
                  </Typography>
                </Box>
              </Box>

              {/* Model Configuration */}
              <Box sx={{ mt: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <FormControl fullWidth>
                    <InputLabel>Models</InputLabel>
                    <Select
                      multiple
                      value={models}
                      onChange={e => {
                        setModels(e.target.value as string[]);
                        markChanged();
                      }}
                      input={<OutlinedInput label="Models" />}
                      renderValue={selected => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map(value => (
                            <Chip key={value} label={MODEL_NAMES[value] || value} size="small" />
                          ))}
                        </Box>
                      )}
                    >
                      {Object.entries(MODEL_NAMES).map(([code, name]) => (
                        <MenuItem key={code} value={code}>
                          <Checkbox checked={models.indexOf(code) > -1} />
                          <ListItemText primary={name} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      setModels(['gpt-4o', 'claude-4-5-sonnet']);
                      markChanged();
                    }}
                    sx={{ whiteSpace: 'nowrap', minWidth: 'auto' }}
                  >
                    Legacy 😮‍💨
                  </Button>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                  LLM models used for generating notes with this template
                </Typography>
              </Box>
            </Collapse>
          </CardContent>
        </Card>

        {/* Sections Editor */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6">
                Sections ({sections.length})
              </Typography>
              <Button
                variant="outlined"
                startIcon={<Add />}
                onClick={handleAddSection}
              >
                Add Section
              </Button>
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Drag sections to reorder. Click the expand button to edit section details.
            </Typography>

            {sections.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                  No sections defined. Click "Add Section" to create one.
                </Typography>
              </Box>
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={sectionIds}
                  strategy={verticalListSortingStrategy}
                >
                  {sections.map((section, index) => (
                    <SortableSectionItem
                      key={section.id}
                      section={section}
                      index={index}
                      onUpdate={handleUpdateSection}
                      onRemove={handleRemoveSection}
                    />
                  ))}
                </SortableContext>
              </DndContext>
            )}
          </CardContent>
        </Card>

        {/* Bottom Save Button */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 4 }}>
          <Button
            variant="contained"
            color="primary"
            size="large"
            startIcon={isUpdating ? <CircularProgress size={20} color="inherit" /> : <Save />}
            onClick={handleSave}
            disabled={isUpdating || !hasChanges || !canSavePublicTemplate}
          >
            {isUpdating ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      </main>

      {/* Version History Drawer */}
      <VersionHistoryDrawer
        open={historyDrawerOpen}
        onClose={() => setHistoryDrawerOpen(false)}
        templateId={templateId}
        currentVersion={templateData?.revision.version ?? 1}
        onCompare={handleCompareVersions}
      />

      {/* Shadow Note Test Dialog */}
      <ShadowNoteTestDialog
        open={shadowNoteDialogOpen}
        onClose={() => setShadowNoteDialogOpen(false)}
        templateId={templateId}
        templateTitle={title || templateData?.revision.title || 'Untitled'}
        templateVersion={templateData?.revision.version ?? 1}
        organizationId={templateData?.template.organization_id}
        providerId={providerId}
      />
    </div>
  );
}
